import { trackRSProductClicked } from '@app/analytics';
import {
  EmbeddedAddToCartButton,
  EmbeddedAddToCartCallback,
  embeddedAddToCartStyleVariant,
} from '@app/components/EmbeddedAddToCartButton/EmbeddedAddToCartButton';
import {
  MatureContentCover,
  MatureContentCoverStyle,
} from '@app/components/MatureContentCover/MatureContentCover';
import { priceStyleVariant } from '@app/components/Price/Price';
import { ProcessedSearchResultItem } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import { useSRPImagePreloadLimits } from '@app/hooks/useSRPImagePreloadLimits';
import { usePrefersReducedMotion } from '@app/pages/i/components/ProductConfigurator/usePrefersReducedMotion';
import imageScaleStyles from '@app/styles/productCardImageScale.module.scss';
import { cookieStorage } from '@app/utils/cookieStorage';
import { getPriceComponent } from '@app/utils/getPriceComponent';
import searchUUIDStorage, {
  SearchUUIDStorageKey,
} from '@app/utils/searchUUIDStorage';
import { Box, Card, Text } from '@redbubble/design-system';
import { useViewportWidth } from '@redbubble/next/hooks/use-viewport-width';
import { useIntl } from '@redbubble/next/intl';
import { AppProps, DeviceType } from '@redbubble/next/types';
import {
  AnalyticsItemListID,
  AnalyticsItemsList,
  AnalyticsStorage,
  AnalyticsStorageKeys,
} from '@redbubble/next-analytics/gtm';
import { usePageProps } from '@redbubble/next-provider';
import {
  type RSPageTypeKey,
  type RSProductClickedLocationKey,
  useRudderstackClient,
} from '@redbubble/next-provider/rudderstack';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import React from 'react';
import {
  ObserverInstanceCallback,
  useInView,
} from 'react-intersection-observer';
import { EstablishedArtistCaption } from '../../../../components/EstablishedArtistCaption/EstablishedArtistCaption';
import { LAST_CLICKED_ITEM_ID_COOKIE } from '../../constants';
import type { SearchResultsPageProps } from '../../types';
import { messages } from '../messages';
import { AddToListButton } from './AddToListButton/AddToListButton';
import { HideLinkMediaCover } from './HideLinkMediaCover';
import styles from './SearchResultCard.module.scss';

export type InViewCallback = ObserverInstanceCallback | null;

interface SearchResultCardProps {
  inventoryItem: ProcessedSearchResultItem;
  artistIsEstablished: boolean;
  imageSrc: string;
  addItemToCart: EmbeddedAddToCartCallback;
  onInView: InViewCallback | null;
  index: number;
  analyticsItemIndex: number;
  onMouseDown?: (event: React.MouseEvent) => void;
  groupId?: string | null;
  hideLink?: boolean;
  rsLocation?: RSProductClickedLocationKey;
  rsPageType?: RSPageTypeKey;
}

const loadToast = () => import('react-toastify').then((module) => module.toast);

const storeAnalyticsData = (
  analyticsComponentID: AnalyticsItemListID,
  analyticsIndex?: number
) => {
  AnalyticsStorage.setValue(
    AnalyticsStorageKeys.RefListID,
    analyticsComponentID
  );
  if (Number.isInteger(analyticsIndex))
    AnalyticsStorage.setValue(
      AnalyticsStorageKeys.RefListIndex,
      `${analyticsIndex}`
    );
};

const AddToCartToast = dynamic(
  () =>
    import(
      /* webpackChunkName: "AddToCartToast" */
      '../../../../components/Toasts/AddToCartToast'
    ).then(({ AddToCartToast: Component }) => Component),
  { ssr: false, loading: () => null }
);

const showATCToast = async (
  productTypeId,
  gaCategory,
  description,
  attributes,
  imageSrc,
  isMobile
) => {
  const toast = await loadToast();
  if (isMobile) {
    toast.dismiss();
  }

  toast(
    <AddToCartToast
      productTypeId={productTypeId}
      gaCategory={gaCategory}
      description={description}
      attributes={attributes}
      imageSrc={imageSrc}
    />
  );
};

const noOp = () => {};

export const SearchResultCard: React.FC<SearchResultCardProps> = ({
  inventoryItem,
  groupId,
  artistIsEstablished,
  imageSrc,
  addItemToCart,
  onInView,
  index,
  analyticsItemIndex,
  onMouseDown,
  hideLink,
  rsLocation,
  rsPageType,
}) => {
  const mouseDownEvent = (event: React.MouseEvent) => {
    storeAnalyticsData(
      AnalyticsItemsList.SearchResultsPageResults,
      analyticsItemIndex
    );
    if (onMouseDown) onMouseDown(event);
    if (link && searchUUID) searchUUIDStorage.set(link, searchUUID);
  };

  const intl = useIntl();
  const { searchUUID, deviceType, listsInclusions } = usePageProps<
    AppProps & SearchResultsPageProps
  >();

  const prefersReducedMotion = usePrefersReducedMotion();

  const { isOrAbove } = useViewportWidth(deviceType);
  const isMobile = !isOrAbove(DeviceType.Tablet);

  const imagePreloadLimit = useSRPImagePreloadLimits(deviceType);
  const imagePriority = index < imagePreloadLimit;

  const { ref } = useInView({
    skip: !onInView,
    triggerOnce: true,
    onChange: onInView || noOp,
  });

  const {
    id,
    description,
    price,
    volumeDiscount,
    productPageUrl,
    experiencesProductCard,
    attributes,
    gaCategory,
    productTypeId,
  } = inventoryItem;

  const { RudderstackClient } = useRudderstackClient();

  const link = productPageUrl ? new URL(productPageUrl).pathname : undefined;
  if (!link) return null;

  const { work } = inventoryItem;
  const { artistShopDisplayName: artistName, matureContent, title } = work;
  const volumeDiscountThresholds = volumeDiscount?.thresholds || [];

  const altText =
    title && artistName
      ? intl.formatMessage(messages.previewAltText, {
          title,
          artistName,
        })
      : intl.formatMessage(messages.previewAltTextFallback);

  const cardCaption = artistIsEstablished ? (
    <EstablishedArtistCaption caption={artistName ?? undefined} />
  ) : (
    <Text muted lineClamp={1} type="body2">
      {artistName}
    </Text>
  );

  const quickATC = experiencesProductCard.some(
    (experience) => experience.value === 'quickAddToCart'
  );

  const handleClick = () => {
    cookieStorage.setItem(LAST_CLICKED_ITEM_ID_COOKIE, id);
    if (rsLocation && rsPageType) {
      trackRSProductClicked({
        rsClient: RudderstackClient,
        location: rsLocation,
        pageType: rsPageType,
      });
    }
  };

  return (
    <Box ref={ref} data-testid="search-result-card">
      <HideLinkMediaCover hideLink={hideLink} link={link}>
        <Box
          onMouseDown={mouseDownEvent}
          onClick={() => handleClick()}
          className={styles.link}
          element="a"
          href={hideLink ? undefined : link}
          data-testid="search-results-page-product-card"
        >
          {/* using NONE is a hack to enforce no elevation - this is not a defined constant */}
          <Card elevation="NONE">
            <MatureContentCover
              matureContent={matureContent}
              aspectRatioHeight={1}
              styleType={MatureContentCoverStyle.SearchResultsPageProductCard}
            >
              <Box display="flex" className={styles.imageContainer}>
                <Box className={styles.addToListMobile}>
                  <AddToListButton
                    inventoryItem={inventoryItem}
                    workId={work.id}
                    listsInclusions={listsInclusions}
                    analyticsComponentID={
                      AnalyticsItemsList.SearchResultsPageResults
                    }
                    analyticsIndex={analyticsItemIndex}
                    searchUUIDStorageKey={SearchUUIDStorageKey.Latest}
                    isMobile
                  />
                </Box>
                {imageSrc && (
                  <Image
                    className={[
                      styles.productCardImage,
                      styles.searchResultRatio,
                      imageScaleStyles.productImageScale,
                      imageScaleStyles[productTypeId || ''],
                      groupId ? imageScaleStyles.groupId : '',
                      !prefersReducedMotion ? imageScaleStyles.imageHover : '',
                    ].join(' ')}
                    src={imageSrc}
                    alt={altText}
                    height={600}
                    width={600}
                    // TODO: Remove after react upgrade, check tests pass
                    fetchPriority={imagePriority ? 'high' : undefined}
                    priority={imagePriority}
                    placeholder="blur"
                    // this is a random base64 string that is a gray transparent image
                    blurDataURL="data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw=="
                    unoptimized
                  />
                )}
              </Box>
            </MatureContentCover>
            <Box paddingBottom="s" paddingTop="s">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="start"
                className={styles.textAndActionContainer}
              >
                <Box className={styles.titleBlock}>
                  <Text
                    lineClamp={1}
                    type="body2"
                    display="block"
                    className={styles.title}
                  >
                    {`${title} ${description}`}
                  </Text>
                  <Box className={styles.disableLineHeight}>{cardCaption}</Box>
                </Box>
                <Box className={styles.addToListDesktop}>
                  <AddToListButton
                    inventoryItem={inventoryItem}
                    workId={work.id}
                    listsInclusions={listsInclusions}
                    analyticsComponentID={
                      AnalyticsItemsList.SearchResultsPageResults
                    }
                    analyticsIndex={analyticsItemIndex}
                    searchUUIDStorageKey={SearchUUIDStorageKey.Latest}
                  />
                </Box>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                paddingRight="s"
                paddingTop="s"
              >
                <Box paddingRight="s">
                  {getPriceComponent(
                    volumeDiscountThresholds,
                    price,
                    priceStyleVariant.SEARCH_RESULTS_PAGE_PRODUCT_CARD
                  )}
                </Box>
                {quickATC && (
                  <EmbeddedAddToCartButton
                    id={id}
                    addItemToCart={addItemToCart}
                    notify={(event) => {
                      event.preventDefault();
                      showATCToast(
                        productTypeId,
                        gaCategory,
                        description,
                        attributes,
                        imageSrc,
                        isMobile
                      );
                    }}
                    index={index}
                    variant={
                      embeddedAddToCartStyleVariant.SEARCH_RESULTS_PAGE_PRODUCT_CARD
                    }
                  />
                )}
              </Box>
            </Box>
          </Card>
        </Box>
      </HideLinkMediaCover>
    </Box>
  );
};
